import React, { Fragment } from "react";
import Link from "next/link";
import CommonContainer from "components/SecondaryContainer";
import clsx from "clsx";
import { useLocale } from "hooks";
import { useHeader } from "components/PageContainer/PageContext";
import { useQuery } from "react-query";
import Head from "next/head";

const LinkWrapper = ({ link, title, index, arr = [], isLast = false }) => {
  const { locale } = useLocale();
  const { toggleMenu, setMenuChain, setActiveKeys } = useHeader();
  const { data: menu } = useQuery([locale, "menu"], { enabled: false });

  const getIndex = (item: any, inputIndex: number) => {
    return item?.children?.findIndex(
      (item) => item.title === arr?.[inputIndex]?.title
    );
  };

  const handleClick = () => {
    if (isLast) return;
    const newChain = [];
    const activeKeys = [];
    toggleMenu();

    (function generator(item, i: number) {
      newChain.push(item?.children);
      const theIndex = getIndex(item, i + 1);
      const theItem = item?.children?.[theIndex];
      if (theItem) {
        activeKeys.push(theIndex);
        if (index > i &&  [-1, 0].includes(i)) {
          generator(theItem, i + 1);
        }
      }
    })({ children: menu?.[0] }, -1);

    setMenuChain(newChain);
    setActiveKeys(activeKeys);
  };

  return link ? (
    <Link href={link} passHref prefetch={false} hrefLang={locale}>
      <a
        className={clsx("tw-text-[13px] xxxl:tw-text-[17.33px] tw-text-start", {
          "tw-text-[#344456]/60": !isLast,
          "tw-text-[#344456]": isLast,
        })}
      >
        {title}
      </a>
    </Link>
  ) : (
    <button
      className={clsx(
        "tw-bg-zero tw-border-0 tw-text-[13px] xxxl:tw-text-[17.33px] tw-text-start",
        {
          "tw-text-[#344456]/60 tw-cursor-pointer": !isLast,
          "tw-text-[#344456]": isLast,
        }
      )}
      onClick={handleClick}
    >
      {title}
    </button>
  );
};

function Arrow({ hidden }) {
  const { isRTL } = useLocale();

  return !isRTL ? (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={clsx({ "tw-hidden": hidden })}
    >
      <path
        d="M7.14844 6.175L10.9734 10L7.14844 13.825L8.33177 15L13.3318 10L8.33177 5L7.14844 6.175Z"
        fill="#344456"
        fillOpacity="0.6"
      />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={clsx({ "tw-hidden": hidden })}
    >
      <path
        d="M12.8516 6.175L9.02656 10L12.8516 13.825L11.6682 15L6.66823 10L11.6682 5L12.8516 6.175Z"
        fill="#344456"
        fillOpacity="0.6"
      />
    </svg>
  );
}

export default function BreadCrumb({
  className,
  items = [], // [..,{title:'',link:''},..]
  ...props
}) {
  const { isFA } = useLocale();

  const [HOME_LINK, HOME_TITLE] = isFA ? ["/", "صفحه اصلی"] : ["/en", "Home"];

  return (
    <div className={className} {...props}>
      <CommonContainer>
        <div className="tw-flex tw-gap-x-1 tw-flex-wrap tw-items-center tw-py-5 tw-px-4 md:tw-px-0">
          <LinkWrapper
            link={HOME_LINK}
            title={HOME_TITLE}
            isLast={items.length === 0}
          />
          {items?.map?.((v, k, arr) => (
            <Fragment key={v.title}>
              <Arrow />
              <LinkWrapper
                link={v.link}
                title={v.title}
                isLast={items.length - 1 === k}
                index={k}
                arr={arr}
              />
            </Fragment>
          ))}
        </div>
      </CommonContainer>
      <Head>
        <script type="application/ld+json">
          {`{
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": ${JSON.stringify(
              [
                { title: HOME_TITLE, link: HOME_LINK },
                ...(Array.isArray(items) ? items : []),
              ]?.map((item, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: item?.title?.replaceAll?.('"', "") || "",
                item: item?.link
                  ? `khatam.ac.ir${item?.link?.replaceAll?.('"', "") || ""}`
                  : "#",
              })) || []
            )}
          }`}
        </script>
      </Head>
    </div>
  );
}
